import { Org } from '../../types/dynamodb-tables';
import type { EmailHealthResponse } from '../../types/email-health';
import { createApiClient } from './apiClient';
import type { OneRosterConfig } from './onerosterService';

interface DistrictOrgData {
  country?: string;
  state?: string;
  name: string;
  phoneNumber?: string;
  classification?: string;
  employerProvider?: string;
  secondary?: boolean;
  primary?: boolean;
  adult?: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  isTMAllowed?: boolean;
  emailDomain?: string | null;
  onerosterConfig?: OneRosterConfig;
}

interface SchoolOrgData {
  name: string;
  address?: string;
  city?: string;
  phoneNumber?: string;
  parentOrgId: string;
  country?: string;
  state?: string;
  classificationLevel?: number | null;
}

interface SearchParams {
  name?: string;
  noChamps?: boolean;
  parentOrgId?: string;
  showLastActive?: boolean;
}

interface MidasParams {
  orgId: string;
  startDate: string;
  endDate: string;
}



// Organization API Service
export const organizationService = {
  // Create a district organization
  createDistrictOrg: async (districtData: DistrictOrgData): Promise<Org> => {
    const apiClient = createApiClient();

    // Build payload, only include phoneNumber if it's not empty
    const payload: {
      classification: string;
      country: string;
      state: string;
      name: string;
      employerProvider?: string;
      secondary?: boolean;
      primary?: boolean;
      adult?: boolean;
      firstName?: string;
      lastName?: string;
      email?: string;
      isTMAllowed?: boolean;
      phoneNumber?: string;
      emailDomain?: string | null;
      onerosterConfig?: OneRosterConfig;
    } = {
      classification: districtData.classification || 'school-district',
      country: districtData.country || 'US',
      state: districtData.state || '',
      name: districtData.name,
      employerProvider: districtData.employerProvider,
      secondary: districtData.secondary,
      primary: districtData.primary,
      adult: districtData.adult,
      firstName: districtData.firstName,
      lastName: districtData.lastName,
      email: districtData.email,
      isTMAllowed: districtData.isTMAllowed,
    };

    // Only include phoneNumber if it's provided and not empty
    if (districtData.phoneNumber && districtData.phoneNumber.trim()) {
      payload.phoneNumber = districtData.phoneNumber;
    }

    if (districtData.emailDomain !== undefined) {
      payload.emailDomain = districtData.emailDomain;
    }

    // Include OneRoster configuration if provided
    if (districtData.onerosterConfig) {
      payload.onerosterConfig = districtData.onerosterConfig;
    }

    const response = await apiClient.post('/org', payload);
    // API returns { message, org }, extract the org object
    return response.data.org || response.data;
  },

  // Search for district organizations
  searchDistrictOrgs: async (params: SearchParams = {}): Promise<Org[]> => {
    const apiClient = createApiClient();
    const response = await apiClient.get('/org/search', {
      params: {
        classification: 'school-district',
        noChamps: params.noChamps || false,
        ...(params.showLastActive ? { showLastActive: true } : {}),
      },
    });
    return response.data;
  },

  // Create a school organization
  createSchoolOrg: async (schoolData: SchoolOrgData): Promise<Org> => {
    const apiClient = createApiClient();

    const payload: {
      classification: string;
      name: string;
      address: string;
      city: string;
      phoneNumber: string;
      parentOrgId: string;
      country: string;
      state: string;
      classificationLevel?: number | null;
    } = {
      classification: 'school',
      name: schoolData.name,
      address: schoolData.address || '',
      city: schoolData.city || '',
      phoneNumber: schoolData.phoneNumber || '',
      parentOrgId: schoolData.parentOrgId,
      country: schoolData.country || 'US',
      state: schoolData.state || '',
    };

    // Only include classificationLevel if it's explicitly provided (including null)
    if (schoolData.classificationLevel !== undefined) {
      payload.classificationLevel = schoolData.classificationLevel;
    }

    const response = await apiClient.post('/org', payload);
    // API returns { message, org }, extract the org object
    return response.data.org || response.data;
  },

  // Search for school organizations
  searchSchoolOrgs: async (params: SearchParams = {}): Promise<Org[]> => {
    // Return empty array if parentOrgId is not provided
    // This prevents API errors when tm_admin logs in without a selected district
    // Fixes: "Unknown classification school" error when parentOrgId is empty
    if (!params.parentOrgId) {
      return [];
    }
    
    const apiClient = createApiClient();
    const response = await apiClient.get('/org/search', {
      params: {
        classification: 'school',
        parentOrgId: params.parentOrgId,
      },
    });
    return response.data;
  },

  // Get organization by ID
  getOrgById: async (orgId: string): Promise<Org> => {
    const apiClient = createApiClient();
    const response = await apiClient.get('/org', {
      params: { orgId },
    });
    return response.data;
  },

  // Update organization
  updateOrg: async (orgData: Partial<Org>): Promise<Org> => {
    const apiClient = createApiClient();
    
    const response = await apiClient.put('/org', orgData);
    return response.data;
  },

  // Get organization data
  getOrgData: async (orgId: string): Promise<Record<string, unknown>> => {
    const apiClient = createApiClient();
    const response = await apiClient.get('/org/data', {
      params: { orgId },
    });
    return response.data;
  },

  // delete organization data
  deleteOrg: async (orgId: string): Promise<{ message: string; status: number }> => {
    const apiClient = createApiClient();
    try {
      const response = await apiClient.delete(`/org/${orgId}`);
      return response.data;
    } catch (error) {
      console.log('[organizationService][deleteOrg] Error:', error)
      console.error(`Error deleting organization ${orgId}:`, error);
      throw error;
    }
  },

  // Generate data for MIDAS
  generateMidasData: async (params: MidasParams): Promise<Record<string, unknown>> => {
    const apiClient = createApiClient();
    const response = await apiClient.get('/org/generateMidasData', {
      params,
    });
    return response.data;
  },

  /**
   * District (or school→district) email domain health + recent bounces.
   * Uses a longer timeout — large districts can approach the 60s server limit.
   */
  getEmailHealth: async (orgId: string): Promise<EmailHealthResponse> => {
    const apiClient = createApiClient();
    const response = await apiClient.get('/org/email-health', {
      params: { orgId },
      timeout: 60000,
    });
    return response.data;
  },
}; 